import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link as MuiLink,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TrashRow } from 'shared';
import { api } from '../api';
import { Note } from '../components/Note';
import { formatDateTime } from '../format';
import { MONO, tokens } from '../theme';

/**
 * Thùng rác — spec SC-13 §2.1.
 *
 * Trước spec này, xoá một trang là xoá vĩnh viễn kèm toàn bộ lịch sử điểm
 * (`onDelete: Cascade` trên bảng `Scan`). Sau một năm quét hàng tuần đó là 52 điểm
 * dữ liệu — chính là "giá trị chính của việc theo dõi lâu dài" mà change request
 * §2.2 nhấn mạnh. Bấm nhầm là mất, không hoàn tác được.
 */

/**
 * Hộp thoại xoá vĩnh viễn.
 *
 * Nêu con số THẬT về số lần quét sẽ mất, không nói chung chung: "sẽ mất dữ liệu"
 * không giúp ai quyết định, còn "mất 52 lần quét đã lưu" thì có.
 */
function PurgeDialog({ row, onClose }: { row: TrashRow | null; onClose: () => void }) {
  const qc = useQueryClient();
  const xoa = useMutation({
    mutationFn: () => api.purgePage(row!.slug),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['trash'] });
      void qc.invalidateQueries({ queryKey: ['registry'] });
      onClose();
    },
  });

  return (
    <Dialog open={!!row} onClose={xoa.isPending ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 18 }}>Xoá vĩnh viễn?</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography sx={{ fontSize: 14 }}>
            Trang <b>{row?.code}</b> sẽ bị xoá hẳn khỏi hệ thống.
          </Typography>
          <Note tone="bad">
            Sẽ mất <b>{row?.scanCount} lần quét đã lưu</b> cùng toàn bộ lịch sử điểm của trang này.
            Không hoàn tác được.
          </Note>
          {row && row.conLaiNgay > 0 && (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
              Không làm gì thì hệ thống tự xoá sau {row.conLaiNgay} ngày nữa.
            </Typography>
          )}
          {xoa.error && <Note tone="bad">{(xoa.error as Error).message}</Note>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit" disabled={xoa.isPending}>
          Huỷ
        </Button>
        <Button color="error" variant="contained" disabled={xoa.isPending} onClick={() => xoa.mutate()}>
          {xoa.isPending ? 'Đang xoá…' : 'Xoá vĩnh viễn'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function TrashPage() {
  const qc = useQueryClient();
  const [sapXoa, setSapXoa] = useState<TrashRow | null>(null);

  const { data, isLoading, error } = useQuery({ queryKey: ['trash'], queryFn: api.trash });

  const khoiPhuc = useMutation({
    mutationFn: (slug: string) => api.restorePage(slug),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['trash'] });
      void qc.invalidateQueries({ queryKey: ['registry'] });
    },
  });

  return (
    <Box>
      <Typography sx={{ fontSize: 11.5, letterSpacing: '.14em', color: tokens.teal, fontWeight: 600 }}>
        THÙNG RÁC
      </Typography>
      <Typography variant="h5" sx={{ fontSize: 28, color: tokens.ink, mt: 0.5 }}>
        Trang đã xoá
      </Typography>
      <Typography sx={{ color: 'text.secondary', maxWidth: '70ch', mt: 0.5, mb: 3 }}>
        Xoá một trang không làm mất lịch sử điểm của nó. Trang nằm ở đây 30 ngày rồi hệ thống tự
        xoá hẳn; trong thời gian đó khôi phục lại được đầy đủ.
      </Typography>

      {error && <Note tone="bad">{(error as Error).message}</Note>}
      {khoiPhuc.error && <Note tone="bad">{(khoiPhuc.error as Error).message}</Note>}

      <Paper variant="outlined" sx={{ p: { xs: 1.6, md: 2.2 } }}>
        {isLoading ? (
          <Stack spacing={1}>
            {Array.from({ length: 3 }, (_, i) => (
              <Skeleton key={i} variant="rounded" height={44} />
            ))}
          </Stack>
        ) : data?.length ? (
          <TableContainer sx={{ overflow: 'visible' }}>
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 190 }}>Trang</TableCell>
                  <TableCell>Địa chỉ</TableCell>
                  <TableCell sx={{ width: 145 }}>Xoá lúc</TableCell>
                  <TableCell sx={{ width: 130 }}>Người xoá</TableCell>
                  <TableCell align="right" sx={{ width: 95 }}>
                    Lần quét
                  </TableCell>
                  <TableCell sx={{ width: 105 }}>Tự xoá sau</TableCell>
                  <TableCell align="right" sx={{ width: 175 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((r) => (
                  <TableRow key={r.id} sx={{ '&:nth-of-type(even) td': { bgcolor: tokens.rowAlt } }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: 14 }}>{r.code}</TableCell>
                    <TableCell
                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      <MuiLink
                        href={r.publicUrl}
                        target="_blank"
                        rel="noreferrer"
                        sx={{ fontFamily: MONO, fontSize: 12.5, color: 'text.secondary' }}
                      >
                        {r.publicUrl}
                      </MuiLink>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5 }}>{formatDateTime(r.deletedAt)}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{r.deletedByName ?? '—'}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: MONO, fontSize: 13 }}>
                      {r.scanCount}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: 12.5,
                        // Sắp bị dọn thì tô cảnh báo — đây là cơ hội cuối để khôi phục.
                        color: r.conLaiNgay <= 3 ? tokens.roseText : 'text.secondary',
                        fontWeight: r.conLaiNgay <= 3 ? 600 : 400,
                      }}
                    >
                      {r.conLaiNgay} ngày
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Button
                          size="small"
                          startIcon={<RestoreIcon sx={{ fontSize: 16 }} />}
                          disabled={khoiPhuc.isPending}
                          onClick={() => khoiPhuc.mutate(r.slug)}
                        >
                          Khôi phục
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteForeverIcon sx={{ fontSize: 16 }} />}
                          onClick={() => setSapXoa(r)}
                        >
                          Xoá hẳn
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Note>Thùng rác trống.</Note>
        )}
      </Paper>

      <PurgeDialog row={sapXoa} onClose={() => setSapXoa(null)} />
    </Box>
  );
}
